/** Destination search: shared saved places, debounced geocoding, recents. */

import type { LngLat, Place } from '../core/types';
import { searchPlaces, searchPlacesAlongRoute, searchPlacesWithAi } from '../search/geocode';
import { listRecents } from '../core/db';
import { formatDistance } from '../core/format';
import { store } from '../core/store';
import {
  ApiError,
  createFolder,
  deleteFolder,
  fetchFolders,
  setPlaceFolder,
  type SavedPlace,
} from '../auth/api';
import { distance, projectPointOntoLine } from '../core/geo';
import { t } from '../core/i18n';
import { cleanSharedPlaceText } from '../search/shareTarget';

export class SearchPanel {
  readonly el: HTMLElement;
  private input!: HTMLInputElement;
  private list!: HTMLElement;
  private debounce: number | null = null;
  private inflight: AbortController | null = null;
  private revision = 0;
  private requestTimer: ReturnType<typeof setTimeout> | null = null;
  private focusTimer: ReturnType<typeof setTimeout> | null = null;
  private sharedImport: 'ready' | 'unsupported' | null = null;
  /** Folders the fleet has, newest list from the server. */
  private folders: string[] = [];
  /**
   * Folders the driver has opened this session - local to this device.
   *
   * Folders start CLOSED. Filing is for putting things away, so the useful
   * default is a short list of folder names you can scan, not every customer
   * the fleet has ever saved unrolled above the recents. What you open stays
   * open until the app restarts.
   */
  private expanded = new Set<string>();

  constructor(
    parent: HTMLElement,
    private getOrigin: () => LngLat | null,
    /** Active route geometry while choosing an intermediate stop. */
    private getRoute: () => LngLat[] | null,
    private onPick: (place: Place) => void,
    private onClose: () => void,
    private getSaved: () => SavedPlace[],
    private onDeleteSaved: (id: string) => Promise<void>,
    /** Open the rename dialog for a saved place; resolves once renamed. */
    private onRenameSaved: (place: SavedPlace) => Promise<void>,
    /** Re-pull the shared places after one has been filed somewhere else. */
    private onSavedChanged: () => Promise<void>,
  ) {
    this.el = document.createElement('div');
    this.el.className = 'panel panel--search';
    this.el.hidden = true;
    this.el.innerHTML = `
      <div class="panel__bar">
        <button class="icon-btn" data-act="close" aria-label="Back">
          <svg viewBox="0 0 24 24"><path d="M15 4 L7 12 L15 20" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
        <input class="panel__input" type="search" placeholder="${t('search.ph')}"
               autocomplete="off" autocorrect="off" spellcheck="false" enterkeyhint="search"/>
        <button class="search-submit" type="button" data-act="submit">Search</button>
      </div>
      <div class="panel__list" role="listbox"></div>`;
    parent.appendChild(this.el);

    this.input = this.el.querySelector('.panel__input')!;
    this.list = this.el.querySelector('.panel__list')!;
    this.el.querySelector('[data-act="submit"]')!.addEventListener('click', () => void this.runSearch());
    this.el.querySelector('[data-act=close]')!.addEventListener('click', () => this.onClose());
    this.input.addEventListener('input', () => {
      this.sharedImport = null;
      this.queueSearch();
    });
    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.runSearch();
      }
    });
  }

  /**
   * Open a fresh search, or reveal the exact query/results hidden while a
   * destination was being previewed.
   */
  open(preserveResults = false): void {
    this.el.hidden = false;
    if (!preserveResults) {
      this.cancelSearch();
      const revision = this.revision;
      this.sharedImport = null;
      this.input.value = '';
      // Folders first, then the list - otherwise the first paint files everything
      // as unfiled and visibly re-shuffles a moment later.
      void this.refreshFolders().then(() => {
        if (revision === this.revision && !this.el.hidden && !this.input.value) void this.showRecents();
      });
    } else if (!this.list.childElementCount) {
      void this.showRecents();
    }
    // Delay focus so the open transition isn't janked by the keyboard.
    this.focusTimer = setTimeout(() => { if (!this.el.hidden) this.input.focus(); }, 180);
  }

  setPlaceholder(text: string): void {
    this.input.placeholder = text;
  }

  /**
   * Open a provider-neutral search from text received through `/share/`.
   * Nothing is selected here: the driver must choose a Mapbox/local row and
   * then gets the ordinary route preview before navigation can begin.
   */
  openSharedImport(query: string | null): void {
    this.cancelSearch();
    this.sharedImport = query ? 'ready' : 'unsupported';
    this.input.value = query ?? '';
    if (query) void this.runSearch();
    else this.list.innerHTML = this.sharedImportNoticeHtml();
    this.focusTimer = setTimeout(() => { if (!this.el.hidden) this.input.focus(); }, 180);
  }

  close(preserveResults = false): void {
    if (this.focusTimer !== null) clearTimeout(this.focusTimer);
    if (this.debounce !== null) clearTimeout(this.debounce);
    this.debounce = null;
    this.el.hidden = true;
    this.input.blur();
    // A result can be selected while the remote half of the search is still
    // loading. Keep that request alive for the preview-back path so returning
    // reveals the complete list, not a frozen "Searching…" row.
    if (!preserveResults) {
      this.cancelSearch();
    }
  }

  private queueSearch(): void {
    this.cancelSearch();
    this.debounce = window.setTimeout(() => this.runSearch(), 450);
  }

  private async runSearch(): Promise<void> {
    this.cancelSearch();
    const revision = this.revision;
    const request = new AbortController();
    this.inflight = request;
    const raw = this.input.value.trim();
    // Do not resolve provider URLs or send their IDs/coordinates to a geocoder.
    // Shared human-readable names become a fresh search that needs selection.
    const q = /https?:\/\/|www\./i.test(raw) ? cleanSharedPlaceText([raw]) ?? '' : raw;
    if (raw && !q) this.sharedImport = 'unsupported';
    if (q.length < 2) {
      if (this.sharedImport === 'unsupported') {
        this.list.innerHTML = this.sharedImportNoticeHtml();
        return;
      }
      void this.showRecents();
      return;
    }
    // "showrom" finds "Showroom" before any network round-trip. Recents are
    // searched in full (name AND address, the whole history), so a place
    // driven to months ago is findable without the geocoder.
    const saved = this.getSaved();
    const recents = await listRecents(500);
    if (revision !== this.revision) return;
    const activeLine = this.getRoute();
    const localSaved = saved
      .map((p) => ({
        p,
        score: fuzzyScore(q, p.name),
        routeOffset: activeLine
          ? projectPointOntoLine([p.lng, p.lat], activeLine)?.distance ?? Infinity
          : Infinity,
      }))
      .filter((x) => x.score > 0)
      .sort((a, b) =>
        activeLine ? a.routeOffset - b.routeOffset || b.score - a.score : b.score - a.score,
      )
      .slice(0, 4);
    const localRecent = this.rankAlongRoute(recents)
      .map((p) => ({
        p,
        score: Math.max(fuzzyScore(q, p.name), p.address ? fuzzyScore(q, p.address) : 0),
      }))
      .filter((x) => x.score > 0)
      .sort((a, b) =>
        activeLine
          ? (a.p.routeOffset ?? Infinity) - (b.p.routeOffset ?? Infinity) || b.score - a.score
          : b.score - a.score,
      )
      .slice(0, 5);

    const localHtml =
      this.sharedImportNoticeHtml() +
      localSaved.map((x) => this.savedRow(x.p)).join('') +
      this.placesHtml(localRecent.map((x) => x.p), true);
    this.list.innerHTML = localHtml + `<div class="panel__hint">${t('search.searching')}</div>`;
    this.bindSaved(saved);
    this.bindPlaceRows(localRecent.map((x) => x.p));

    try {
      const route = this.getRoute();
      const places = route
        ? await searchPlacesAlongRoute(q, route, this.getOrigin(), request.signal)
        : await searchPlaces(
            q,
            this.getOrigin(),
            request.signal,
            // Shared provider text is resolved independently by Mapbox. It
            // does not trigger a second provider lookup in Wayfarer.
            this.sharedImport !== 'ready',
          );
      if (revision !== this.revision) return;
      // Avoid duplicating a remote hit that's already shown as a local match.
      const localIds = new Set(localRecent.map((x) => x.p.id));
      const remote = places.filter((p) => !localIds.has(p.id));
      const foursquareRemote = remote.filter((place) => place.provider === 'foursquare');
      const otherRemote = remote.filter((place) => place.provider !== 'foursquare');
      const nothing = !remote.length && !localSaved.length && !localRecent.length;
      this.list.innerHTML = nothing
        ? `<div class="panel__hint">${t('search.noResults')}</div>${this.aiButtonHtml(q)}`
        : localHtml +
          this.foursquarePlacesHtml(foursquareRemote) +
          this.placesHtml(otherRemote, false) +
          this.aiButtonHtml(q);
      this.bindSaved(saved);
      this.bindPlaceRows([
        ...localRecent.map((x) => x.p),
        ...foursquareRemote,
        ...otherRemote,
      ]);
      this.bindAiButton(q);
    } catch (err) {
      if (revision !== this.revision) return;
      const failHtml = `<div class="panel__hint">${store.get().online ? t('search.failed') : t('search.offline')}</div>`;
      this.list.innerHTML = localHtml + failHtml;
      this.bindSaved(saved);
      this.bindPlaceRows(localRecent.map((x) => x.p));
    } finally {
      if (revision === this.revision && this.requestTimer !== null) clearTimeout(this.requestTimer);
    }
  }

  /*
   * "Ask AI" sits UNDER the ordinary results, never in place of them.
   *
   * The plain search is instant, free and usually right; the model is for the
   * times it is not - a half-remembered name, a description instead of a name,
   * a spelling that only works if you say it aloud. Offering it as a choice
   * rather than running it automatically also means it only costs something
   * when the driver decides it is worth it.
   */
  private aiButtonHtml(q: string): string {
    if (q.length < 3) return '';
    return `
      <button class="ai-search" data-ai="${escapeHtml(q)}">
        <span class="ai-search__spark">${SPARK_SVG}</span>
        <span class="ai-search__text">
          <span class="ai-search__title">${t('search.ai')}</span>
          <span class="ai-search__sub">${t('search.aiSub')}</span>
        </span>
      </button>`;
  }

  private bindAiButton(q: string): void {
    const btn = this.list.querySelector<HTMLButtonElement>('[data-ai]');
    btn?.addEventListener('click', () => void this.runAiSearch(q));
  }

  private async runAiSearch(q: string): Promise<void> {
    this.cancelSearch();
    const revision = this.revision;
    const request = new AbortController();
    this.inflight = request;
    this.requestTimer = setTimeout(() => request.abort(), 15_000);
    this.list.innerHTML = `<div class="panel__hint">${t('search.aiThinking')}</div>`;
    try {
      const places = this.rankAlongRoute(
        await searchPlacesWithAi(q, this.getOrigin(), request.signal),
      );
      if (revision !== this.revision) return;
      if (!places.length) {
        this.list.innerHTML = `<div class="panel__hint">${t('search.aiNothing')}</div>`;
        return;
      }
      this.list.innerHTML =
        `<div class="panel__hint panel__hint--ai">${t('search.aiFound')}</div>` +
        this.placesHtml(places, false);
      this.bindPlaceRows(places);
    } catch (err) {
      if (revision !== this.revision) return;
      // 503 = no key on this server; say that plainly rather than "failed".
      const offConfig = err instanceof ApiError && err.status === 503;
      const limited = err instanceof ApiError && err.status === 429;
      this.list.innerHTML = `<div class="panel__hint">${
        offConfig ? t('search.aiOff') : limited ? t('search.aiLimit') : t('search.aiFailed')
      }</div>`;
    } finally {
      if (revision === this.revision && this.requestTimer !== null) clearTimeout(this.requestTimer);
    }
  }

  private savedRow(p: SavedPlace): string {
    const origin = this.getOrigin();
    const units = store.get().settings.units;
    const iconClass = p.kind === 'shared-location' ? 'live' : (p.category ?? 'star');
    const route = this.getRoute();
    const routeOffset = route
      ? projectPointOntoLine([p.lng, p.lat], route)?.distance
      : undefined;
    return `
      <div class="place place--saved" data-saved="${p.id}">
        <span class="place__icon place__icon--${iconClass}">${categoryIcon(p)}</span>
        <span class="place__text">
          <span class="place__name">${escapeHtml(p.name)}</span>
          <span class="place__addr">${t('search.by', { name: escapeHtml(p.creatorName) })}</span>
        </span>
        ${
          routeOffset != null
            ? `<span class="place__dist">${t('search.offRoute', { d: formatDistance(routeOffset, units) })}</span>`
            : origin
              ? `<span class="place__dist">${formatDistance(distance(origin, [p.lng, p.lat]), units)}</span>`
            : ''
        }
        <button class="place__move" data-movesaved="${p.id}" aria-label="${t('folder.move')}">
          ${FOLDER_SVG}
        </button>
        <button class="place__ren" data-rensaved="${p.id}" aria-label="Rename saved place">
          <svg viewBox="0 0 24 24"><path d="M4 20 L8.5 19 L19.5 8 a1.8 1.8 0 0 0 -2.5 -2.5 L6 16.5 Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/></svg>
        </button>
        <button class="place__del" data-delsaved="${p.id}" aria-label="Delete saved place">&#215;</button>
      </div>`;
  }

  /*
   * Saved places grouped under their folders.
   *
   * Folders are a flat list of names the fleet shares - "Sauna customers",
   * "Shops" - because a driver filing a customer wants everyone to find it
   * there, not just themselves. Unfiled places keep their own group at the top
   * so nothing becomes invisible by not having been filed yet, and an empty
   * folder is still listed: you make the folder first and fill it afterwards.
   */
  /**
   * Is this place in a folder that actually exists?
   *
   * A folder name that is no longer in the list reads as unfiled rather than
   * vanishing into a group nothing renders - deleting a folder must never be
   * able to hide a place.
   */
  private isFiled(p: SavedPlace): boolean {
    const f = (p.folder ?? '').toLowerCase();
    return f !== '' && this.folders.some((x) => x.toLowerCase() === f);
  }

  private savedByFolderHtml(saved: SavedPlace[]): string {
    const visible = saved.filter((p) => p.kind !== 'shared-location');
    const byFolder = new Map<string, SavedPlace[]>();
    for (const p of visible) {
      const key = this.isFiled(p) ? (p.folder as string) : '';
      const list = byFolder.get(key) ?? [];
      list.push(p);
      byFolder.set(key, list);
    }

    const groups: string[] = [];
    const unfiled = byFolder.get('') ?? [];
    if (unfiled.length) {
      groups.push(
        `<div class="panel__section">${t('search.saved')}</div>` +
          unfiled.map((p) => this.savedRow(p)).join(''),
      );
    }
    for (const folder of this.folders) {
      const items = byFolder.get(folder) ?? [];
      const open = this.expanded.has(folder);
      groups.push(`
        <div class="folder ${open ? 'is-open' : ''}">
          <button class="folder__head" data-folder="${escapeHtml(folder)}">
            <span class="folder__chevron">${CHEVRON_SVG}</span>
            <span class="folder__name">${escapeHtml(folder)}</span>
            <span class="folder__count">${items.length}</span>
            <span class="folder__del" data-delfolder="${escapeHtml(folder)}"
                  role="button" aria-label="${t('folder.delete')}">&#215;</span>
          </button>
          ${
            open
              ? items.length
                ? items.map((p) => this.savedRow(p)).join('')
                : `<div class="folder__empty">${t('folder.empty')}</div>`
              : ''
          }
        </div>`);
    }

    groups.push(
      `<button class="folder-add" data-newfolder="1">＋ ${t('folder.new')}</button>`,
    );
    return groups.join('');
  }

  private bindFolders(saved: SavedPlace[]): void {
    // Collapse / expand.
    this.list.querySelectorAll<HTMLElement>('[data-folder]').forEach((head) => {
      head.addEventListener('click', (e) => {
        if ((e.target as HTMLElement).closest('[data-delfolder]')) return;
        const name = head.dataset.folder!;
        if (this.expanded.has(name)) this.expanded.delete(name);
        else this.expanded.add(name);
        void this.showRecents();
      });
    });

    this.list.querySelectorAll<HTMLElement>('[data-delfolder]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const name = btn.dataset.delfolder!;
        // Say plainly that the places survive - otherwise this looks like it
        // deletes the customers filed inside it.
        if (!confirm(t('folder.deleteConfirm', { name }))) return;
        void deleteFolder(name)
          .then(() => this.refreshFolders())
          .then(() => this.showRecents())
          .catch((err) => alert(err instanceof Error ? err.message : ''));
      });
    });

    this.list.querySelector<HTMLElement>('[data-newfolder]')?.addEventListener('click', () => {
      const name = prompt(t('folder.newPrompt'))?.trim();
      if (!name) return;
      void createFolder(name)
        .then(() => this.refreshFolders())
        .then(() => this.showRecents())
        .catch((err) => alert(err instanceof Error ? err.message : ''));
    });

    // Move a place into a folder.
    this.list.querySelectorAll<HTMLButtonElement>('[data-movesaved]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const p = saved.find((x) => x.id === btn.dataset.movesaved);
        if (p) this.openMoveSheet(p);
      });
    });
  }

  /**
   * Where a place should live. A list of every folder plus "no folder", rather
   * than a text box, so filing is one tap and cannot invent a near-duplicate
   * folder through a typo. Creating one from here is still offered.
   */
  private openMoveSheet(place: SavedPlace): void {
    const current = (place.folder ?? '').toLowerCase();
    const rows = [
      `<button class="move-opt ${current === '' ? 'is-current' : ''}" data-to="">
         ${t('folder.none')}
       </button>`,
      ...this.folders.map(
        (f) => `
        <button class="move-opt ${current === f.toLowerCase() ? 'is-current' : ''}"
                data-to="${escapeHtml(f)}">${escapeHtml(f)}</button>`,
      ),
      `<button class="move-opt move-opt--new" data-to-new="1">＋ ${t('folder.new')}</button>`,
    ].join('');

    const sheet = document.createElement('div');
    sheet.className = 'move-sheet';
    sheet.innerHTML = `
      <div class="move-sheet__card">
        <div class="move-sheet__head">${t('folder.moveTitle', { name: escapeHtml(place.name) })}</div>
        <div class="move-sheet__list">${rows}</div>
        <button class="btn btn--ghost move-sheet__cancel">${t('preview.cancel')}</button>
      </div>`;
    this.el.appendChild(sheet);

    const close = (): void => sheet.remove();
    sheet.addEventListener('click', (e) => {
      if (e.target === sheet) close();
    });
    sheet.querySelector('.move-sheet__cancel')!.addEventListener('click', close);
    sheet.querySelector<HTMLElement>('[data-to-new]')!.addEventListener('click', () => {
      const name = prompt(t('folder.newPrompt'))?.trim();
      if (!name) return;
      close();
      void setPlaceFolder(place.id, name)
        .then(() => this.refreshFolders())
        .then(() => this.onSavedChanged())
        .then(() => this.showRecents())
        .catch((err) => alert(err instanceof Error ? err.message : ''));
    });
    sheet.querySelectorAll<HTMLElement>('[data-to]').forEach((opt) => {
      opt.addEventListener('click', () => {
        close();
        void setPlaceFolder(place.id, opt.dataset.to || null)
          .then(() => this.refreshFolders())
          .then(() => this.onSavedChanged())
          .then(() => this.showRecents())
          .catch((err) => alert(err instanceof Error ? err.message : ''));
      });
    });
  }

  /** Pull the folder list; failure just means the groups render as before. */
  async refreshFolders(): Promise<void> {
    try {
      const { folders } = await fetchFolders();
      this.folders = folders;
    } catch {
      /* keep whatever we had */
    }
  }

  /**
   * One-tap shortcuts: Home, Showroom, Depot… sorted by category.
   *
   * UNFILED places only. Filing a customer into a folder is the act of putting
   * it away, so repeating it in the shortcut row directly above defeats the
   * point - the row filled up with filed customers and pushed the everyday
   * places it exists for off the end.
   */
  private quickChipsHtml(saved: SavedPlace[]): string {
    const chips = saved
      .filter((p) => p.kind !== 'shared-location' && !this.isFiled(p))
      .sort(
        (a, b) =>
          (CATEGORY_ORDER[a.category ?? 'other'] ?? 9) - (CATEGORY_ORDER[b.category ?? 'other'] ?? 9),
      )
      .slice(0, 8);
    if (!chips.length) return '';
    return `<div class="quick-chips">${chips
      .map(
        (p) => `
      <button class="quick-chip" data-quick="${p.id}">
        ${categoryIcon(p)}<span>${escapeHtml(p.name)}</span>
      </button>`,
      )
      .join('')}</div>`;
  }

  private async showRecents(): Promise<void> {
    const revision = this.revision;
    const recents = await listRecents();
    if (revision !== this.revision || this.input.value.trim().length >= 2 || this.el.hidden) return;
    const saved = this.getSaved();
    if (!recents.length && !saved.length) {
      this.list.innerHTML = `<div class="panel__hint">${t('search.empty')}</div>`;
      return;
    }

    const savedHtml = saved.length ? this.quickChipsHtml(saved) + this.savedByFolderHtml(saved) : '';

    this.list.innerHTML = savedHtml + (recents.length ? `<div class="panel__section">${t('search.recent')}</div>` : '');
    this.bindSaved(saved);
    this.bindFolders(saved);
    if (recents.length) this.appendPlaces(recents, true);
  }

  private bindSaved(saved: SavedPlace[]): void {
    this.list.querySelectorAll<HTMLButtonElement>('[data-quick]').forEach((chip) => {
      chip.addEventListener('click', () => {
        const p = saved.find((x) => x.id === chip.dataset.quick);
        if (p) {
          this.onPick({
            id: `saved:${p.id}`,
            name: p.name,
            address: t('place.savedBy', { name: p.creatorName }),
            center: [p.lng, p.lat],
            kind: 'saved',
          });
        }
      });
    });
    this.list.querySelectorAll<HTMLElement>('.place--saved').forEach((row) => {
      row.addEventListener('click', (e) => {
        if ((e.target as HTMLElement).closest('.place__del, .place__ren, .place__move')) return;
        const p = saved.find((x) => x.id === row.dataset.saved);
        if (p) {
          this.onPick({
            id: `saved:${p.id}`,
            name: p.name,
            address: t('place.savedBy', { name: p.creatorName }),
            center: [p.lng, p.lat],
            kind: 'saved',
          });
        }
      });
    });
    this.list.querySelectorAll<HTMLButtonElement>('[data-delsaved]').forEach((btn) => {
      btn.addEventListener('click', () => {
        void this.onDeleteSaved(btn.dataset.delsaved!)
          .then(() => this.showRecents())
          .catch((err) => alert(err instanceof Error ? err.message : t('search.deleteFailed')));
      });
    });
    this.list.querySelectorAll<HTMLButtonElement>('[data-rensaved]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const p = saved.find((x) => x.id === btn.dataset.rensaved);
        if (!p) return;
        void this.onRenameSaved(p)
          .then(() => this.showRecents())
          .catch(() => {});
      });
    });
  }

  private appendPlaces(places: Place[], recent: boolean): void {
    const frag = document.createElement('div');
    frag.innerHTML = this.placesHtml(places, recent);
    while (frag.firstChild) this.list.appendChild(frag.firstChild);
    this.bindPlaceRows(places);
  }

  private cancelSearch(): void {
    this.revision++;
    if (this.requestTimer !== null) clearTimeout(this.requestTimer);
    this.requestTimer = null;
    if (this.debounce !== null) clearTimeout(this.debounce);
    this.debounce = null;
    this.inflight?.abort();
    this.inflight = null;
  }

  private sharedImportNoticeHtml(): string {
    if (this.sharedImport === 'ready') {
      return `<div class="panel__hint panel__hint--shared" data-shared-import="ready" role="status">${t('search.sharedReady')}</div>`;
    }
    if (this.sharedImport === 'unsupported') {
      return `<div class="panel__hint panel__hint--shared" data-shared-import="unsupported" role="status">${t('search.sharedUnsupported')}</div>`;
    }
    return '';
  }

  /** Add route offset/position and put corridor-near places first. */
  private rankAlongRoute(places: Place[]): Place[] {
    const route = this.getRoute();
    if (!route?.length) return places;
    return places
      .map((place) => {
        const projected = projectPointOntoLine(place.center, route);
        return {
          ...place,
          routeOffset: projected?.distance,
          routeAlong: projected?.along,
        };
      })
      .sort(
        (a, b) =>
          (a.routeOffset ?? Infinity) - (b.routeOffset ?? Infinity) ||
          (a.routeAlong ?? Infinity) - (b.routeAlong ?? Infinity),
      );
  }

  private placesHtml(places: Place[], recent: boolean): string {
    const units = store.get().settings.units;
    // When nothing matched the street, say what to do about it rather than
    // leaving the driver to work out why the answers look wrong.
    const hint =
      places.length && places.every((p) => p.approximate)
        ? `<div class="place-hint">${t('search.approxHint')}</div>`
        : '';
    const rows = places
      .map(
        (p, i) => `
        <button class="place ${p.provider === 'foursquare' ? 'place--foursquare' : ''} ${p.approximate ? 'is-approx' : ''}"
                role="option" data-wayfarer-place="${i}" data-provider="${p.provider ?? ''}">
          <span class="place__icon">${recent ? CLOCK_SVG : resultIcon(p)}</span>
          <span class="place__text">
            <span class="place__name">${escapeHtml(p.name)}</span>
            <span class="place__addr">${escapeHtml(p.address)}</span>
            ${
              p.approximate
                ? `<span class="place__warn">${t('search.approx')}</span>`
                : ''
            }
          </span>
          ${
            p.routeOffset != null
              ? `<span class="place__dist">${t('search.offRoute', { d: formatDistance(p.routeOffset, units) })}</span>`
              : p.distance != null
                ? `<span class="place__dist">${formatDistance(p.distance, units)}</span>`
                : ''
          }
        </button>`,
      )
      .join('');
    return hint + rows;
  }

  /** Foursquare coordinates enter the same Wayfarer preview flow as any POI. */
  private foursquarePlacesHtml(places: Place[]): string {
    if (!places.length) return '';
    return `
      <section class="foursquare-place-results" data-foursquare-results>
        <div class="panel__section">${t('search.foursquareTitle')}</div>
        ${this.placesHtml(places, false)}
        <div class="place-provider-attribution">
          ${t('search.resultsBy')} <strong translate="no">Foursquare</strong>
        </div>
      </section>`;
  }

  private bindPlaceRows(places: Place[]): void {
    // Bind by DOM order (matches the order rows were rendered), not data-i -
    // concatenated lists would otherwise collide on indices. `onclick` keeps
    // re-binding idempotent.
    this.list
      .querySelectorAll<HTMLButtonElement>('button.place[data-wayfarer-place]')
      .forEach((btn, i) => {
      const place = places[i];
      if (place) btn.onclick = () => this.onPick(place);
    });
  }
}

const PIN_SVG = `<svg viewBox="0 0 24 24"><path d="M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z" fill="currentColor"/></svg>`;
const STAR_SVG = `<svg viewBox="0 0 24 24"><path d="M12 3 L14.7 8.6 L20.9 9.4 L16.4 13.7 L17.5 19.8 L12 16.9 L6.5 19.8 L7.6 13.7 L3.1 9.4 L9.3 8.6 Z" fill="currentColor"/></svg>`;
const LIVE_SVG = `<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="4" fill="currentColor"/><path d="M5 12 a7 7 0 0 1 14 0 M8 12 a4 4 0 0 1 8 0" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>`;
const HOME_SVG = `<svg viewBox="0 0 24 24"><path d="M4 11 L12 4 L20 11 M6 10 V19 a1 1 0 0 0 1 1 h3 v-5 h4 v5 h3 a1 1 0 0 0 1-1 V10" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const WORK_SVG = `<svg viewBox="0 0 24 24"><rect x="4" y="8" width="16" height="11" rx="1.5" fill="none" stroke="currentColor" stroke-width="2"/><path d="M9 8 V6 a1.5 1.5 0 0 1 1.5-1.5 h3 A1.5 1.5 0 0 1 15 6 v2 M4 13 h16" fill="none" stroke="currentColor" stroke-width="2"/></svg>`;
const SHOWROOM_SVG = `<svg viewBox="0 0 24 24"><path d="M4 9 L5 4 h14 l1 5 M5 9 v10 h14 V9 M9 19 v-6 h6 v6" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/><path d="M3 9 h18" stroke="currentColor" stroke-width="2"/></svg>`;
const DEPOT_SVG = `<svg viewBox="0 0 24 24"><path d="M3 9 L12 4 L21 9 V20 H3 Z M7 20 v-7 h10 v7 M7 16 h10" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/></svg>`;

export function categoryIcon(p: { kind: string; category?: string }): string {
  if (p.kind === 'shared-location') return LIVE_SVG;
  switch (p.category) {
    case 'home':
      return HOME_SVG;
    case 'work':
      return WORK_SVG;
    case 'showroom':
      return SHOWROOM_SVG;
    case 'depot':
      return DEPOT_SVG;
    default:
      return STAR_SVG;
  }
}

/** Order for quick chips: named categories first, ad-hoc saves after. */
const CATEGORY_ORDER: Record<string, number> = { home: 0, work: 1, showroom: 2, depot: 3, other: 4 };

/** Simple fuzzy score: substring beats prefix beats spread subsequence. */
export function fuzzyScore(query: string, text: string): number {
  const q = query.toLowerCase().trim();
  const s = text.toLowerCase();
  if (!q) return 0;
  if (s.startsWith(q)) return 100;
  if (s.includes(q)) return 80;
  let qi = 0;
  let gaps = 0;
  let started = false;
  for (const ch of s) {
    if (qi < q.length && ch === q[qi]) {
      qi++;
      started = true;
    } else if (started && qi < q.length) {
      gaps++;
    }
  }
  return qi === q.length ? Math.max(10, 60 - gaps * 2) : 0;
}
const CLOCK_SVG = `<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="2"/><path d="M12 7v5l3.5 2" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`;

const PLANE_SVG = `<svg viewBox="0 0 24 24"><path d="M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5z" fill="currentColor"/></svg>`;
const FOLDER_SVG = `<svg viewBox="0 0 24 24"><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linejoin="round"/></svg>`;
const CHEVRON_SVG = `<svg viewBox="0 0 24 24"><path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const SPARK_SVG = `<svg viewBox="0 0 24 24"><path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" fill="currentColor"/><path d="M18.5 14.5l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8z" fill="currentColor" opacity="0.75"/></svg>`;
const PARK_SVG = `<svg viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="4" fill="none" stroke="currentColor" stroke-width="2"/><path d="M9 17V7h3.4a3.3 3.3 0 0 1 0 6.6H9" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

/**
 * A plane for an airport, a P for a car park - recognised before the text is
 * read, which is the point when the list is scanned at a set of lights.
 */
function resultIcon(p: Place): string {
  if (p.kind === 'airport' || p.kind === 'airport-terminal') return PLANE_SVG;
  if (p.kind === 'airport-parking') return PARK_SVG;
  return PIN_SVG;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}
